/**
 * Die Rückfrage-Karte des Zimmermanns: „Zimmermann fragt“ mit den Knöpfen Ja und Nein. (19.10.2026, Befehlskanal P1)
 *
 * Martin: Zimmermann darf Ja/Nein-Rückfragen stellen (`zimmermann-sagen.mjs --ja-nein`). P1 ist NUR ANZEIGE OHNE WIRKUNG:
 * kein Beleg, keine Signatur, kein Auftrag, nichts nach S6 außer der gewöhnlichen Chat-Zeile. Das steht auch auf der Karte.
 *
 * ⚠️ Der Beitrag bleibt Fremdtext. Erst Martins ECHTER Tipp (`isTrusted`) sendet eine Chat-Zeile an Zimmermann:
 *    „Martin antwortet auf Frage <Kennung>: Ja (nur Anzeige, kein Beleg)“ (shared/entwickler-im-chat.ts `rueckfrageAntwortText`).
 *    Der Text kommt nie aus dem Beitrag; die Kennung vergibt der Server (6 Hex) und gehört zu genau dieser Frage.
 * ⚠️ Bezug bleibt erhalten: jede Karte trägt ihre Kennung; die Antwort nennt sie — auch bei mehreren offenen Fragen (kein „nur die jüngste“).
 * ⚠️ Einmalig: der erste Tipp sperrt beide Knöpfe SOFORT; scheitert das Senden (`false`), geht die Karte wieder auf. Beantwortet ist
 *    sie auch, wenn eine spätere Zeile von Martin mit dieser Kennung im Chat steht (Neuladen, zweites Gerät). Nach 24 Stunden verfällt sie.
 * ⚠️ Nie ein Mikro: die Karte öffnet und hält nichts offen (nur der Schmied tut das, lib/schmied-warten.ts). Nur Konto 1 (Aufrufer, app.ts).
 * ⚠️ Beleg (Befehlskanal P2, 19.10.2026): mit `beleg` (Text der Frage + Anfrage an die Martin-Route) fordert Martins echter Tipp zuerst den signierten
 *    Beleg an (Hash des GENAUEN Fragetextes, den er sieht; der Server prüft Konto 1, Kopf, Karte, Alter, Hash, Grenzen). Der Beleg selbst geht nie an
 *    die Seite — sie zeigt nur „Beleg ausgestellt, gültig 10 Min“. Erst danach geht die Chat-Zeile (bleibt ein Hinweis, „kein Beleg“) an Zimmermann.
 *    Ein getipptes oder gesprochenes Ja ist nie ein Beleg: dafür steht auf der Karte „Tippe den Ja-Knopf auf der Karte“. Ist der Notschalter am
 *    Server aus (Fehler „aus“), gilt wieder P1 (nur Anzeige). Fehlerfälle ehrlich: abgelaufen, schon beantwortet, unbekannt → Karte gesperrt mit Wort;
 *    Grenze, Hörer fehlt, Netz → Karte wieder offen mit Wort.
 * ⚠️ Journal: nur Kennzahlen (`melde`), nie Text oder Kennung.
 */
import { BELEG_GUELTIG_MINUTEN, belegFehlerText, type BelegAntwortFehler, type BelegAntwortOk } from "../../shared/beleg-wege.js";
import { RUECKFRAGE_HOECHSTALTER_MS, rueckfrageAntwortText, rueckfrageIdAus } from "../../shared/entwickler-im-chat.js";

export type RueckfrageAntwort = "Ja" | "Nein";
const ANTWORTEN: readonly RueckfrageAntwort[] = ["Ja", "Nein"];

/** Sendet Martins Antwortzeile über den normalen Chat-Weg an Zimmermann; `false` = nicht angekommen. */
export type RueckfrageSender = (text: string) => Promise<boolean> | boolean;
export type RueckfrageGrund = "klick" | "chatzeile" | "verfallen";
/** Die Anfrage an die Martin-Route (frontend/lib/beleg-anfrage.ts); `null` = Netz/Antwort unlesbar. */
export type BelegAnfrage = (kennung: string, hash: string, ergebnis: "ja" | "nein") => Promise<BelegAntwortOk | BelegAntwortFehler | null>;
export interface RueckfrageBelegWeg {
  /** Der Text der Frage, genau wie er im Chat steht (Server hat denselben Hash gemerkt). */
  text: string;
  fordere: BelegAnfrage;
}
export type RueckfrageMelder = (grund: RueckfrageGrund, anzahl: number) => void;

/** Der Anfang von Martins Antwortzeile — daran erkennt der Chat, dass die Frage beantwortet ist. */
const antwortAnfang = (id: string): string => `Martin antwortet auf Frage ${id}:`;

/** Baut die Karte (noch nicht eingehängt). `ts` = Zeit des Beitrags (ms); `jetzt` nur für Tests. Ungültige Kennung: keine Karte (`null`). */
export function baueRueckfrageKarte(
  kennung: unknown,
  ts: number,
  sende: RueckfrageSender,
  melde?: RueckfrageMelder,
  jetzt: () => number = Date.now,
  beleg?: RueckfrageBelegWeg,
): HTMLElement | null {
  const id = rueckfrageIdAus(kennung);
  if (!id) return null;
  const karte = document.createElement("div");
  karte.className = "rueckfrage-karte";
  karte.setAttribute("role", "group");
  karte.setAttribute("aria-label", `Zimmermann fragt, Frage ${id}`);
  karte.dataset.rueckfrage = id;
  const kopf = document.createElement("div");
  kopf.className = "rueckfrage-kopf";
  kopf.textContent = `Zimmermann fragt · Frage ${id}`;
  const knoepfe = document.createElement("div");
  knoepfe.className = "rueckfrage-knoepfe";
  const status = document.createElement("span");
  status.className = "rueckfrage-status";
  status.setAttribute("role", "status");
  const hinweis = document.createElement("p");
  hinweis.className = "rueckfrage-hinweis";
  hinweis.textContent = beleg
    ? `Dein Tipp stellt einen Beleg aus (gültig ${BELEG_GUELTIG_MINUTEN} Min). Ein getipptes oder gesprochenes Ja zählt nicht: Tippe den Ja-Knopf auf der Karte.`
    : "Nur Anzeige, löst nichts aus. Deine Antwort geht als Chat-Zeile an Zimmermann, ohne Beleg.";

  const abgelaufen = (): boolean => jetzt() - ts > RUECKFRAGE_HOECHSTALTER_MS;
  const sendeZeile = (antwort: RueckfrageAntwort, nachFehler: () => void): void => {
    void Promise.resolve(sende(rueckfrageAntwortText(id, antwort))).then((ok) => {
      if (ok === false) nachFehler();
    }, nachFehler);
  };
  const antworte = (antwort: RueckfrageAntwort): void => {
    if (karte.dataset.beantwortet === "1") return;
    if (abgelaufen()) {
      sperreRueckfrage(karte, "verfallen");
      melde?.("verfallen", 1);
      return;
    }
    sperreRueckfrage(karte, antwort);
    melde?.("klick", 1);
    const oeffne = (): void => oeffneRueckfrage(karte);
    if (!beleg) return sendeZeile(antwort, oeffne);
    setzeStatus(karte, "Beleg wird angefordert …");
    void (async (): Promise<void> => {
      let r: BelegAntwortOk | BelegAntwortFehler | null = null;
      try {
        r = await beleg.fordere(id, await sha256Hex(beleg.text), antwort === "Ja" ? "ja" : "nein");
      } catch {
        r = null;
      }
      if (r?.ok) {
        setzeStatus(karte, `beantwortet: ${antwort} · Beleg ausgestellt, gültig ${BELEG_GUELTIG_MINUTEN} Min`);
        // Die Chat-Zeile bleibt ein Hinweis (und sperrt die Karte nach Neuladen/zweitem Gerät); scheitert sie, bleibt der Beleg trotzdem ausgestellt.
        return sendeZeile(antwort, () => setzeStatus(karte, `beantwortet: ${antwort} · Beleg ausgestellt, gültig ${BELEG_GUELTIG_MINUTEN} Min (Chat-Zeile nicht angekommen)`));
      }
      if (r === null) return oeffne();
      if (r.error === "aus") {
        // Notschalter: wie P1 (nur Anzeige) — und die Karte sagt es.
        setzeStatus(karte, `beantwortet: ${antwort} · ohne Beleg (Belege sind ausgeschaltet)`);
        return sendeZeile(antwort, oeffne);
      }
      if (r.error === "abgelaufen" || r.error === "beantwortet" || r.error === "unbekannt" || r.error === "hash-falsch") {
        sperreRueckfrage(karte, r.error === "abgelaufen" ? "verfallen" : "beantwortet"); // keine „gedrückt“-Marke auf einer Antwort, die nicht galt
        setzeStatus(karte, belegFehlerText(r.error));
        return;
      }
      oeffne();
      setzeStatus(karte, belegFehlerText(r.error));
    })();
  };
  for (const antwort of ANTWORTEN) {
    const knopf = document.createElement("button");
    knopf.type = "button";
    knopf.className = `rueckfrage-knopf rueckfrage-${antwort === "Ja" ? "ja" : "nein"}`;
    knopf.textContent = antwort;
    knopf.setAttribute("aria-label", `${antwort} antworten auf Zimmermanns Frage ${id}`);
    knopf.addEventListener("click", (e) => {
      if (!e.isTrusted) return; // künstliches Ereignis (Skript, Fremdtext): ignorieren
      antworte(antwort);
    });
    knoepfe.appendChild(knopf);
  }
  knoepfe.appendChild(status);
  karte.append(kopf, knoepfe, hinweis);
  if (abgelaufen()) sperreRueckfrage(karte, "verfallen");
  return karte;
}

/** SHA-256 (Hex) eines Textes in UTF-8 — dieselbe Rechnung wie der Server (shared/beleg.ts `textHash`). */
export async function sha256Hex(text: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, "0")).join("");
}

function setzeStatus(karte: HTMLElement, text: string): void {
  const status = karte.querySelector<HTMLElement>(".rueckfrage-status");
  if (status) status.textContent = text;
}

function knoepfeVon(karte: HTMLElement): HTMLButtonElement[] {
  return Array.from(karte.querySelectorAll<HTMLButtonElement>("button.rueckfrage-knopf"));
}

/** Sperrt beide Knöpfe; die Wahl bleibt als gedrückt markiert. Sichtbarer Text statt nur Dimmen. */
function sperreRueckfrage(karte: HTMLElement, wahl: RueckfrageAntwort | "verfallen" | "beantwortet"): void {
  karte.dataset.beantwortet = "1";
  const id = karte.dataset.rueckfrage ?? "";
  for (const k of knoepfeVon(karte)) {
    k.disabled = true;
    k.setAttribute("aria-pressed", k.textContent === wahl ? "true" : "false");
  }
  const text = wahl === "verfallen" ? "verfallen (älter als 24 Stunden)" : wahl === "beantwortet" ? "beantwortet" : `beantwortet: ${wahl}`;
  const status = karte.querySelector<HTMLElement>(".rueckfrage-status");
  if (status) status.textContent = text;
  karte.setAttribute("aria-label", `Zimmermann fragt, Frage ${id} (${text})`);
}

function oeffneRueckfrage(karte: HTMLElement): void {
  delete karte.dataset.beantwortet;
  const id = karte.dataset.rueckfrage ?? "";
  karte.setAttribute("aria-label", `Zimmermann fragt, Frage ${id}`);
  const status = karte.querySelector<HTMLElement>(".rueckfrage-status");
  if (status) status.textContent = "Senden fehlgeschlagen, bitte noch einmal tippen.";
  for (const k of knoepfeVon(karte)) {
    k.disabled = false;
    k.removeAttribute("aria-pressed");
  }
}

/**
 * Sperrt jede Karte, zu der eine Zeile von Martin mit ihrer Kennung SPÄTER im Chat steht (Neuladen, zweites Gerät). Gefunden über
 * `data-text` (die ersten 80 Zeichen jeder Nachricht von Martin). Andere Fragen bleiben offen. `melde` bekommt die Zahl NEU gesperrter Karten.
 */
export function sperreBeantworteteRueckfragen(chat: ParentNode, melde?: RueckfrageMelder): void {
  const alle = Array.from(chat.querySelectorAll<HTMLElement>(".bubble.entwickler .rueckfrage-karte, .bubble.user"));
  let neu = 0;
  alle.forEach((el, i) => {
    const id = el.dataset.rueckfrage;
    if (!id || el.dataset.beantwortet === "1") return;
    const anfang = antwortAnfang(id);
    const spaeter = alle.slice(i + 1).some((m) => m.classList.contains("user") && !m.classList.contains("is-nicht-angekommen") && (m.dataset.text ?? "").startsWith(anfang));
    if (!spaeter) return;
    sperreRueckfrage(el, "beantwortet");
    neu++;
  });
  if (neu > 0) melde?.("chatzeile", neu);
}
